import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

/**
 * Per-page bar-owner product tours (Driver.js).
 *
 * Each entry matches a route and defines its own steps. The current page's tour
 * auto-starts the first time it's visited, and the header help button re-runs
 * it on demand.
 *
 * Steps without an `element` render as a centered intro popover; anchored steps
 * are skipped automatically if their target isn't on screen.
 */

const STORAGE_PREFIX = "bhpr.pageTour.";

const PAGE_TOURS = [
    {
        key: "managers",
        match: (pathname) => /^\/managers\//.test(pathname),
        steps: [
            {
                popover: {
                    title: "Managers",
                    description:
                        "Invite teammates so the people who run your bar can help keep everything up to date."
                }
            },
            {
                element: "[data-tour=\"managers-add\"]",
                popover: {
                    title: "Add a manager",
                    description:
                        "Invite a manager by email here. They'll get a link to set their password and jump right in."
                }
            },
            {
                element: "[data-tour=\"managers-list\"]",
                popover: {
                    title: "Your team",
                    description:
                        "Everyone with access shows up here. Change a person's role or remove them anytime."
                }
            }
        ]
    },
    {
        key: "specials",
        match: (pathname) => /^\/specials\//.test(pathname),
        steps: [
            {
                popover: {
                    title: "Specials",
                    description:
                        "Add your happy hours and weekly drink specials so they show up for people deciding where to go out."
                }
            },
            {
                element: "[data-tour=\"specials-type\"]",
                popover: {
                    title: "Pick what you offer",
                    description:
                        "Choose happy hour, weekly specials, or both \u2014 the form below updates to match."
                }
            },
            {
                element: "[data-tour=\"specials-save\"]",
                popover: {
                    title: "Save your specials",
                    description:
                        "When everything looks right, save to publish your specials to barhoppers."
                }
            },
            {
                element: "[data-tour=\"specials-poster\"]",
                popover: {
                    title: "Event & special posters",
                    description:
                        "Got a promotional poster or flyer? Upload it here so barhoppers can see it on your page \u2014 set an expiration date or mark it as ongoing."
                }
            },
            {
                popover: {
                    title: "Where barhoppers see them",
                    description:
                        "Your specials are accessed by a user swiping left on your page." +
                        `<img src="${process.env.PUBLIC_URL || ""}/swipe_for_specials.png" ` +
                        "alt=\"Swiping left on a bar page to view its specials\" " +
                        "style=\"display:block;height:min(425px,56vh);width:auto;max-width:100%;margin:12px auto 0;border-radius:8px;\" />"
                }
            }
        ]
    },
    {
        key: "promotions",
        match: (pathname) => /^\/promotions\/[\w\d]/.test(pathname),
        steps: [
            {
                popover: {
                    title: "Promotions",
                    description:
                        "Push live promotions \u2014 happy hours, events, last-minute deals \u2014 straight to barhoppers near you."
                }
            },
            {
                element: "[data-tour=\"promotions-add\"]",
                popover: {
                    title: "Create a promotion",
                    description:
                        "Start a new promotion here, set when it runs, and it'll reach nearby barhoppers right away."
                }
            },
            {
                element: "[data-tour=\"promotions-list\"]",
                popover: {
                    title: "Your promotions",
                    description:
                        "Active and past promotions live here. Tap one to edit it or check how it's doing."
                }
            }
        ]
    },
    {
        key: "promotionCreate",
        match: (pathname) => /^\/promotion\//.test(pathname),
        steps: [
            {
                popover: {
                    title: "Create a promotion",
                    description:
                        "Fill in a few details and your promotion goes live to barhoppers near you. Here's a quick rundown."
                }
            },
            {
                element: "[data-tour=\"promo-name\"]",
                popover: {
                    title: "Name it",
                    description:
                        "Give your promotion a short, catchy name \u2014 this is the headline barhoppers see first."
                }
            },
            {
                element: "[data-tour=\"promo-description\"]",
                popover: {
                    title: "Add the details",
                    description:
                        "Describe the deal: what's on offer and anything guests should know before they head over."
                }
            },
            {
                element: "[data-tour=\"promo-timing\"]",
                popover: {
                    title: "Set when it runs",
                    description:
                        "Pick a start time and a duration. Choose \u201cContinual\u201d to keep it running until you turn it off."
                }
            },
            {
                element: "[data-tour=\"promo-preview\"]",
                popover: {
                    title: "Live preview",
                    description:
                        "This is exactly how your promotion will look in the app \u2014 it updates as you type."
                }
            },
            {
                element: "[data-tour=\"promo-save\"]",
                popover: {
                    title: "Go live",
                    description:
                        "When everything looks good, save to publish and activate your promotion."
                }
            }
        ]
    },
    {
        key: "placements",
        match: (pathname) => /^\/premium\/placements\//.test(pathname),
        steps: [
            {
                popover: {
                    title: "Featured placements",
                    description:
                        "Boost your bar to the top of the categories your ideal crowd is browsing."
                }
            },
            {
                element: "[data-tour=\"placements-new\"]",
                popover: {
                    title: "Create a placement",
                    description:
                        "Set up a new featured placement here and pick the dates you want to stand out."
                }
            },
            {
                element: "[data-tour=\"placements-list\"]",
                popover: {
                    title: "Your placements",
                    description: "Track your scheduled and past placements in this list."
                }
            }
        ]
    }
];

export function getPageTour(pathname) {
    return PAGE_TOURS.find((tour) => tour.match(pathname)) || null;
}

function storageKey(key) {
    return `${STORAGE_PREFIX}${key}.v1`;
}

function hasSeenTour(key) {
    try {
        return window.localStorage.getItem(storageKey(key)) === "1";
    } catch {
        return false;
    }
}

function markTourSeen(key) {
    try {
        window.localStorage.setItem(storageKey(key), "1");
    } catch {
        // Storage may be unavailable (private mode); failing to persist just
        // means the tour can show again, which is acceptable.
    }
}

/**
 * Drives per-page bar-owner tours and exposes a manual trigger.
 *
 * Returns `{ tourKey, startTour }`:
 *  - `tourKey` is non-null when the current route has a tour (use it to decide
 *    whether to render the header help button).
 *  - `startTour` re-runs the current page's tour on demand.
 */
export function usePageTour() {
    const { pathname } = useLocation();
    const tour = getPageTour(pathname);
    const tourKey = tour?.key || null;
    const activeRef = useRef(null);

    const run = useCallback((targetTour) => {
        if (!targetTour) return;

        const steps = targetTour.steps.filter(
            (step) => !step.element || document.querySelector(step.element)
        );
        if (steps.length === 0) return;

        if (activeRef.current) {
            activeRef.current.destroy();
            activeRef.current = null;
        }

        const instance = driver({
            showProgress: true,
            allowClose: true,
            nextBtnText: "Next",
            prevBtnText: "Back",
            doneBtnText: "Got it",
            steps,
            onDestroyed: () => {
                markTourSeen(targetTour.key);
                activeRef.current = null;
            }
        });

        activeRef.current = instance;
        instance.drive();
    }, []);

    const startTour = useCallback(() => {
        // Defer a tick so a freshly-navigated page has rendered its anchors.
        setTimeout(() => run(tour), 50);
    }, [run, tour]);

    useEffect(() => {
        if (!tourKey) return undefined;

        if (hasSeenTour(tourKey)) return undefined;

        // `cancelled` keeps this safe under React StrictMode's double-mount and
        // when navigating away before the tour starts.
        let cancelled = false;
        const timer = setTimeout(() => {
            if (!cancelled) run(tour);
        }, 700);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (activeRef.current) {
                activeRef.current.destroy();
                activeRef.current = null;
            }
        };
    }, [tourKey, tour, run]);

    return { tourKey, startTour };
}
